import { type CSSProperties, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button, EditArticleButton } from '@/components/atoms';
import { formatNewsDate } from '@/utils/datetime';
import { volumeDeBusca } from '@/utils/trends';
import { gruposDeAssunto } from '@/utils/assunto';
import { boardApi, type BoardCard, type BoardFilters } from '@/services/board';
import { destinationsApi } from '@/services/config';
import type { NewsCard, TopicDetail } from '@/services/intelligence';
import { ArticleEditor } from '@/pages/ArticleEditor';
import { formatarDataHora } from '@/i18n/formato';
import { destinoPadrao, guardarUltimoDestino, ultimoDestino } from '@/config/escopo';

import styles from './Board.module.css';

const ORIGENS = [
  { v: '', label: 'Todas as origens' },
  { v: 'inteligencia', label: 'Inteligência' },
  { v: 'quentes', label: 'Quentes' },
  { v: 'esteira', label: 'Esteira' },
  { v: 'editorias', label: 'Editorias' },
  { v: 'horoscopo', label: 'Horóscopo' },
  { v: 'ion_hub', label: 'TV (ION HUB)' },
  { v: 'strategist_ai', label: 'Estrategista Preditivo' },
  // Mesmo vocabulário de Pauta.fonte / _ROTULOS (backend/app/services/origem.py).
  { v: 'fabrica', label: 'Fábrica' },
  { v: 'vigias', label: 'Vigias' },
  { v: 'trends', label: 'Trends' },
  { v: 'finance', label: 'Finanças' },
];
const METODOS = [
  { v: '', label: 'Todos os métodos' },
  { v: 'ia', label: 'IA' },
  { v: 'framing', label: 'Framing' },
  { v: 'nrcs', label: 'NRCS' },
  { v: 'auto', label: 'Automático' },
];
const RAIAS: { key: 'produzir' | 'produzindo' | 'publicado'; titulo: string }[] = [
  { key: 'produzir', titulo: 'Produzir' },
  { key: 'produzindo', titulo: 'Produzindo' },
  { key: 'publicado', titulo: 'Publicado' },
];

// A régua de volume é uma só, em @/utils/trends — a cópia que morava aqui
// arredondava PARA CIMA (1.500 buscas viravam "2 mil") e o mesmo número
// aparecia diferente nesta tela e na de Inteligência.

// B-01: data curta para o rodapé de rastreabilidade do card
const fmtDT = (iso: string) =>
  formatarDataHora(iso, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function Board() {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  const qc = useQueryClient();
  const [autor, setAutor] = useState(''); // '' = todos · 'me' = meus · email = usuário
  const [fonte, setFonte] = useState('');
  const [metodo, setMetodo] = useState('');
  const [dias, setDias] = useState(0); // 0 = tudo; senão últimos N dias
  const [aberto, setAberto] = useState<BoardCard | null>(null);
  const [cmsCard, setCmsCard] = useState<BoardCard | null>(null); // editor CMS em modal

  const filters: BoardFilters = {
    responsavel: autor || undefined,
    fonte: fonte || undefined,
    metodo: metodo || undefined,
    dias: dias || undefined,
  };

  const boardQ = useQuery({
    queryKey: ['board', filters],
    queryFn: () => boardApi.get(filters),
    refetchInterval: (q) =>
      q.state.data?.columns.produzindo.some((c) => c.estado === 'criando') ? 5000 : false,
  });
  const sitesQ = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => destinationsApi.list(),
    staleTime: 300_000,
  });
  const usuariosQ = useQuery({
    queryKey: ['board', 'usuarios'],
    queryFn: () => boardApi.usuarios(),
    staleTime: 300_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['board'] });
  const close = () => setAberto(null);

  const produzir = useMutation({
    mutationFn: (v: {
      id: number;
      metodo: 'ia' | 'framing' | 'nrcs';
      framming?: string;
      site?: string;
      autopublish?: boolean;
      manter_card?: boolean;
    }) => boardApi.produzir(v.id, v),
    // "manter card" produz um ângulo e deixa o modal aberto para o próximo.
    onSuccess: (_data, v) => { invalidate(); if (!v.manter_card) close(); },
  });
  const retry = useMutation({
    mutationFn: (id: number) => boardApi.retry(id),
    onSuccess: () => { invalidate(); close(); },
  });
  const publicar = useMutation({
    mutationFn: (id: number) => boardApi.publicar(id),
    onSuccess: () => { invalidate(); close(); },
  });
  const deletar = useMutation({
    mutationFn: (id: number) => boardApi.remove(id),
    onSuccess: () => { invalidate(); close(); },
  });

  const cols = boardQ.data?.columns;
  const counts = useMemo(
    () => ({
      produzir: cols?.produzir.length ?? 0,
      produzindo: cols?.produzindo.length ?? 0,
      publicado: cols?.publicado.length ?? 0,
    }),
    [cols],
  );
  // Luz da mesma cor + borda fina para cards do MESMO assunto. Calculado sobre o
  // quadro inteiro, não por raia: as 30 matérias de GTA 6 podem estar espalhadas
  // entre produzir/produzindo/publicado e ainda assim são o mesmo assunto.
  // É informação, não trava — abrir todas continua permitido.
  const grupos = useMemo(
    () => gruposDeAssunto([
      ...(cols?.produzir ?? []), ...(cols?.produzindo ?? []), ...(cols?.publicado ?? []),
    ]),
    [cols],
  );
  const busy = produzir.isPending || retry.isPending || publicar.isPending;

  return (
    <section className={styles.root} aria-label={t('board:title', 'Quadro de Pautas')}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Produção</h1>
          <p className={styles.subtitle}>
            Todo pipeline passa por aqui — Inteligência, Quentes, Esteira, Editorias e
            Horóscopo. O assunto chega completo: clique no card para ver a cobertura.
          </p>
        </div>
        <SweepButton />
      </header>

      <div className={styles.filters}>
        <select
          className={styles.select}
          value={autor}
          onChange={(e) => setAutor(e.target.value)}
          title="Ver a produção por autor"
        >
          <option value="">Todos os autores</option>
          <option value="me">Só os meus</option>
          {(usuariosQ.data ?? []).map((u) => (
            <option key={u.email} value={u.email}>{u.nome || u.email}</option>
          ))}
        </select>
        <select className={styles.select} value={fonte} onChange={(e) => setFonte(e.target.value)}>
          {ORIGENS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select className={styles.select} value={metodo} onChange={(e) => setMetodo(e.target.value)}>
          {METODOS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select
          className={styles.select}
          value={dias}
          onChange={(e) => setDias(Number(e.target.value))}
          title="Filtra por data de criação do card"
        >
          <option value={0}>Todo período</option>
          <option value={1}>Hoje</option>
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
        </select>
        {/* Boolean() é obrigatório: com os filtros vazios a expressão terminava
            em `dias`, que é 0 — e `0 && <jsx>` faz o React RENDERIZAR o zero.
            Era o "0" solto que aparecia na barra ao lado de "Todo período". */}
        {Boolean(autor || fonte || metodo || dias) && (
          <button
            type="button"
            className={styles.clear}
            onClick={() => { setAutor(''); setFonte(''); setMetodo(''); setDias(0); }}
          >
            limpar
          </button>
        )}
        <span className={styles.total}>{boardQ.data?.total ?? 0} no quadro</span>
      </div>

      {boardQ.isError && <p className={styles.error}>Falha ao carregar o quadro.</p>}

      <div className={styles.board}>
        {RAIAS.map((raia) => (
          <div key={raia.key} className={styles.lane}>
            <div className={styles.laneHead}>
              <span>{raia.titulo}</span>
              <span className={styles.laneCount}>{counts[raia.key]}</span>
            </div>
            <div className={styles.laneBody}>
              {(cols?.[raia.key] ?? []).map((card) => (
                <CardView
                  key={card.id}
                  card={card}
                  corDoAssunto={grupos.get(card.id)}
                  onOpen={() => setAberto(card)}
                  onDelete={() => {
                    if (window.confirm(`Deletar o card "${card.tema}"? (não apaga o post no WordPress)`))
                      deletar.mutate(card.id);
                  }}
                />
              ))}
              {(cols?.[raia.key]?.length ?? 0) === 0 && !boardQ.isLoading && (
                <p className={styles.empty}>—</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {aberto && (
        <CardModal
          card={aberto}
          sites={sitesQ.data ?? []}
          busy={busy}
          onClose={close}
          onEditCms={() => { setCmsCard(aberto); setAberto(null); }}
          onProduzir={(metodo, framming, site, autopublish, manterCard) =>
            produzir.mutate({ id: aberto.id, metodo, framming, site, autopublish, manter_card: manterCard })
          }
          onRetry={() => retry.mutate(aberto.id)}
          onPublicar={() => publicar.mutate(aberto.id)}
        />
      )}

      {cmsCard?.wp_post_id && (
        <CmsModal
          postId={cmsCard.wp_post_id}
          site={cmsCard.site ?? undefined}
          onClose={() => { setCmsCard(null); invalidate(); }}
        />
      )}
    </section>
  );
}

// Editor CMS em MODAL (texto pronto no kanban). Botão "expandir" leva à página /artigos/{id}.
function CmsModal({
  postId,
  site,
  onClose,
}: {
  postId: number;
  site?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  return (
    <div className={styles.cmsBackdrop} onClick={onClose}>
      <div className={styles.cmsModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.cmsBar}>
          <span className={styles.cmsTitle}>Editor CMS · #{postId}</span>
          <button
            type="button"
            className={styles.cmsExpand}
            onClick={() => navigate(`/artigos/${postId}${site ? `?site=${site}` : ''}`)}
            title="Abrir na página inteira do editor"
          >
            expandir ↗
          </button>
        </div>
        <div className={styles.cmsBody}>
          <ArticleEditor idProp={postId} siteProp={site} embedded onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

function CardView({
  card, onOpen, onDelete, corDoAssunto,
}: {
  card: BoardCard;
  onOpen: () => void;
  onDelete: () => void;
  /** Cor do grupo quando OUTRO card do quadro trata do mesmo assunto. */
  corDoAssunto?: string;
}) {
  return (
    <div
      className={[
        styles.card,
        card.estado === 'falhou' ? styles.cardFail : '',
        corDoAssunto ? styles.cardAgrupado : '',
      ].filter(Boolean).join(' ')}
      style={corDoAssunto ? ({ '--grupo': corDoAssunto } as CSSProperties) : undefined}
    >
      <button
        type="button"
        className={styles.cardDel}
        title="Deletar card (não apaga o post no WP)"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
      >
        ×
      </button>
      <button type="button" className={styles.cardBody} onClick={onOpen}>
      <p className={styles.cardTitle}>
        {corDoAssunto && (
          <span
            className={styles.luzDoAssunto}
            aria-hidden="true"
            title="Outro card do quadro trata do mesmo assunto — pode publicar do mesmo jeito"
          />
        )}
        {card.tema}
      </p>
      <div className={styles.cardBadges}>
        {card.origem && <Badge tone="info" size="xs">{card.origem}</Badge>}
        {card.metodo && <Badge tone="neutral" size="xs">{card.metodo}</Badge>}
        {card.editoria && <span className={styles.muted}>{card.editoria}</span>}
      </div>
      {(card.score != null || card.search_volume != null) && (
        <div className={styles.metrics}>
          {card.score != null && <span><strong>{card.score}</strong> score</span>}
          {card.search_volume != null && <span>{volumeDeBusca(card.search_volume)} buscas</span>}
          {card.increase_percentage != null && <span>+{card.increase_percentage}%</span>}
        </div>
      )}
      <div className={styles.cardState}>
        {card.estado === 'criando' && (
          <span className={styles.producingInline}><span className={styles.spinner} /> produzindo…</span>
        )}
        {card.estado === 'revisao' && <Badge tone="ok" size="xs">texto pronto</Badge>}
        {card.estado === 'falhou' && <Badge tone="crit" size="xs">falhou</Badge>}
        {card.estado === 'publicada' && <Badge tone="ok" size="xs">publicado</Badge>}
        {card.estado === 'sugerida' && <span className={styles.openHint}>abrir e produzir →</span>}
        {card.produzido_por && (
          <span className={styles.muted} title={card.produzido_por}>
            por {card.produzido_por.split('@')[0]}
          </span>
        )}
      </div>
      {/* B-01: rastreabilidade — quem mandou, quando entrou, quando ficou pronto */}
      {(card.responsavel || card.created_at || card.gerado_em) && (
        <div className={styles.traceRow}>
          <span>
            {card.responsavel
              ? `enviado por ${card.responsavel.split('@')[0]}`
              : 'automático (Sistema)'}
          </span>
          {card.created_at && <span title="entrada no quadro">↳ {fmtDT(card.created_at)}</span>}
          {card.gerado_em && <span title="texto pronto">{fmtDT(card.gerado_em)}</span>}
        </div>
      )}
      </button>
    </div>
  );
}

function CardModal({
  card, sites, busy, onClose, onEditCms, onProduzir, onRetry, onPublicar,
}: {
  card: BoardCard;
  sites: { slug: string; name: string }[];
  busy: boolean;
  onClose: () => void;
  onEditCms: () => void;
  onProduzir: (metodo: 'ia' | 'framing' | 'nrcs', framming: string | undefined, site: string | undefined, autopublish: boolean, manterCard: boolean) => void;
  onRetry: () => void;
  onPublicar: () => void;
}) {
  // card.site manda; sem ele, so ha padrao quando existe um unico destino. Com mais de
  // um, vale o ultimo que esta pessoa escolheu neste navegador (se ainda existir); senao
  // o seletor mostra "escolha o destino" em vez de exibir um site que nao esta escolhido.
  const [site, setSiteState] = useState(
    card.site ?? destinoPadrao(sites) ?? ultimoDestino(sites) ?? '',
  );
  const setSite = (slug: string) => {
    setSiteState(slug);
    guardarUltimoDestino(slug);
  };
  const [angle, setAngle] = useState('');
  const [framing, setFraming] = useState(false);
  const [autopublish, setAutopublish] = useState(false);
  const [manter, setManter] = useState(false); // 1 card → N ângulos
  const [enviados, setEnviados] = useState(0);

  // Dispara a produção; no modo "manter", limpa o ângulo e conta para o próximo.
  const produzirAngulo = (metodo: 'ia' | 'framing' | 'nrcs', framming: string | undefined) => {
    onProduzir(metodo, framming, site || undefined, autopublish, manter);
    if (manter) { setEnviados((n) => n + 1); setAngle(''); }
  };

  const intelQ = useQuery({
    queryKey: ['board', 'intel', card.id],
    queryFn: () => boardApi.intel(card.id),
    enabled: card.tem_intel,
  });
  const detail: TopicDetail | undefined = intelQ.data;

  const grouped = useMemo(() => {
    const map = new Map<string, NewsCard[]>();
    for (const n of detail?.news ?? []) {
      const k = n.source ?? 'Outros';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(n);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [detail?.news]);

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <aside className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.modalHead}>
          <div>
            <h2 className={styles.modalTitle}>{card.tema}</h2>
            <span className={styles.modalSub}>
              {card.origem}
              {card.score != null && ` · score ${card.score}`}
              {card.search_volume != null && ` · ${volumeDeBusca(card.search_volume)} buscas`}
              {card.increase_percentage != null && ` · +${card.increase_percentage}%`}
            </span>
          </div>
          <button type="button" className={styles.modalClose} onClick={onClose}>fechar</button>
        </header>

        <div className={styles.modalBody}>
          <div className={styles.modalMain}>
            {card.tem_intel ? (
              <>
                {intelQ.isLoading && <p className={styles.muted}>Carregando a cobertura…</p>}
                {detail && (
                  <>
                    {detail.motivo && (
                      <p className={styles.verdict}>
                        <strong>{detail.verdict}</strong> — {detail.motivo}
                      </p>
                    )}
                    <div className={styles.section}>
                      <span className={styles.sectionLabel}>
                        Cobertura · {grouped.length} veículos · {detail.news.length} matérias
                      </span>
                      <div className={styles.groups}>
                        {grouped.map(([source, arts]) => (
                          <div key={source} className={styles.group}>
                            <div className={styles.groupHead}>
                              {arts[0]?.source_icon && <img className={styles.newsIcon} src={arts[0].source_icon} alt="" />}
                              <span className={styles.groupName}>{source}</span>
                              <span className={styles.groupCount}>{arts.length}</span>
                            </div>
                            <ul className={styles.groupList}>
                              {arts.map((n, i) => (
                                <li key={n.link ?? `${n.title}-${i}`}>
                                  {n.link ? <a href={n.link} target="_blank" rel="noreferrer">{n.title}</a> : <span>{n.title}</span>}
                                  {n.date && <span className={styles.newsDate}> · {formatNewsDate(n.date)}</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                    {detail.paa.length > 0 && (
                      <div className={styles.section}>
                        <span className={styles.sectionLabel}>People Also Ask</span>
                        <ul className={styles.paa}>{detail.paa.map((q) => <li key={q}>{q}</li>)}</ul>
                      </div>
                    )}
                    {detail.keywords.length > 0 && (
                      <div className={styles.section}>
                        <span className={styles.sectionLabel}>Palavras-chave (SEO)</span>
                        <div className={styles.chips}>{detail.keywords.map((k) => <span key={k} className={styles.kw}>{k}</span>)}</div>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <p className={styles.muted}>Este card não trouxe a cobertura da Inteligência.</p>
            )}
          </div>

          <aside className={styles.modalSide}>
            {card.estado === 'sugerida' && (
              <div className={styles.producePanel}>
                <span className={styles.sideLabel}>Destino</span>
                <select className={styles.select} value={site} onChange={(e) => setSite(e.target.value)}>
                  {sites.length === 0 && <option value="">primário</option>}
                  {sites.length > 0 && !site && <option value="">— escolha o destino —</option>}
                  {sites.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </select>
                <label className={styles.checkRow}>
                  <input type="checkbox" checked={autopublish} onChange={(e) => setAutopublish(e.target.checked)} />
                  publicar direto (senão, rascunho)
                </label>
                <label className={styles.checkRow}>
                  <input type="checkbox" checked={manter} onChange={(e) => setManter(e.target.checked)} />
                  manter card p/ produzir outros ângulos
                </label>
                {enviados > 0 && (
                  <span className={styles.muted}>{enviados} ângulo(s) enviado(s) · o card fica no quadro</span>
                )}
                {!site && (
                  <p className={styles.errMsg}>
                    {sites.length === 0
                      ? 'Sem destino. Cadastre um site em Config → Sites antes de produzir.'
                      : 'Escolha o site de destino antes de produzir.'}
                  </p>
                )}
                <span className={styles.sideLabel}>Método</span>
                {!framing ? (
                  <>
                    <Button size="sm" disabled={busy || !site} onClick={() => produzirAngulo('ia', undefined)}>com IA</Button>
                    <Button size="sm" variant="secondary" disabled={busy || !site} onClick={() => setFraming(true)}>com ângulo (framing)</Button>
                    <Button size="sm" variant="ghost" disabled title="Integração NRCS chega na v3">enviar para NRCS (em breve)</Button>
                  </>
                ) : (
                  <>
                    <input className={styles.angleInput} placeholder="ângulo editorial…" value={angle} onChange={(e) => setAngle(e.target.value)} autoFocus />
                    <Button size="sm" disabled={busy || !angle.trim() || !site} onClick={() => produzirAngulo('framing', angle.trim())}>
                      {manter ? 'gerar ângulo e continuar' : 'gerar com ângulo'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setFraming(false)}>voltar</Button>
                  </>
                )}
              </div>
            )}
            {card.estado === 'criando' && (
              <div className={styles.producing}><span className={styles.spinner} /> agente escrevendo…</div>
            )}
            {card.estado === 'revisao' && (
              <div className={styles.producePanel}>
                <Badge tone="ok" size="xs">texto pronto</Badge>
                {card.wp_post_id ? (
                  <>
                    <EditArticleButton size="sm" onActivate={onEditCms} />
                    <Button size="sm" disabled={busy} onClick={onPublicar}>publicar</Button>
                  </>
                ) : (
                  <p className={styles.errMsg}>Sem rascunho no WP — regere.</p>
                )}
                <Button size="sm" variant="ghost" disabled={busy} onClick={onRetry}>regerar</Button>
              </div>
            )}
            {card.estado === 'falhou' && (
              <div className={styles.producePanel}>
                <Badge tone="crit" size="xs">falhou</Badge>
                {card.erro && <p className={styles.errMsg}>{card.erro}</p>}
                <Button size="sm" disabled={busy} onClick={onRetry}>tentar de novo</Button>
              </div>
            )}
            {card.estado === 'publicada' && card.wp_link && (
              <a href={card.wp_link} target="_blank" rel="noreferrer" className={styles.editLink}>ver no WordPress ↗</a>
            )}
          </aside>
        </div>
      </aside>
    </div>
  );
}

// ─── B-03: varredura de cards legados presos em produção ────────────────────
function SweepButton() {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<import('@/services/board').SweepResult | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const days = 7;

  const scan = useMutation({
    mutationFn: () => boardApi.sweep(days, true),
    onSuccess: (r) => {
      if (r.count === 0) {
        setMsg('Nenhum card preso há mais de 7 dias. Quadro limpo.');
        setPreview(null);
      } else {
        setPreview(r);
        setMsg(null);
      }
    },
  });
  const apply = useMutation({
    mutationFn: () => boardApi.sweep(days, false),
    onSuccess: (r) => {
      setPreview(null);
      setMsg(`${r.count} card(s) removidos do quadro. Posts no WP não foram tocados.`);
      qc.invalidateQueries({ queryKey: ['board'] });
    },
  });

  return (
    <div style={{ textAlign: 'right' }}>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => scan.mutate()}
        disabled={scan.isPending || apply.isPending}
      >
        {scan.isPending ? 'Verificando…' : 'Limpar antigos'}
      </Button>
      {msg && (
        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)', marginTop: 4 }}>{msg}</p>
      )}
      {preview && (
        <div
          style={{
            marginTop: 8,
            padding: 12,
            border: '1px solid var(--border-1)',
            borderRadius: 8,
            background: 'var(--bg-1)',
            maxWidth: 380,
            textAlign: 'left',
          }}
        >
          <strong>
            {preview.count} card(s) presos há mais de {preview.older_than_days} dias, sem post:
          </strong>
          <ul style={{ margin: '8px 0', paddingLeft: 16, fontSize: 'var(--fs-xs)' }}>
            {preview.cards.slice(0, 8).map((c) => (
              <li key={c.id}>
                {c.tema} <em>({c.estado})</em>
              </li>
            ))}
            {preview.count > 8 && <li>… e mais {preview.count - 8}</li>}
          </ul>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={() => apply.mutate()} disabled={apply.isPending}>
              {apply.isPending ? 'Removendo…' : 'Remover todos'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setPreview(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
